import { v4 as uuid } from 'uuid';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { logError } from '@atlassian/jira-project-settings-apps-common/src/util/error-logger.tsx';
import { type OptionUuid, toOptionUuid } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME } from '../../constants.tsx';
import type { FieldOptions, OptionType } from '../../types/layout-items.tsx';
import { doesFieldTypeSupportOptions } from '../../utils/layout-items/does-field-type-support-options.tsx';

const printShape = (option: unknown) => {
	if (option == null || typeof option !== 'object') {
		return `${typeof option}`;
	}

	// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'. | TS2339 - Property 'value' does not exist on type 'object'.
	return `{ id: ${typeof option.id}, value: ${typeof option.value} }`;
};

// Some field options do not have a defined `externalUuid` e.g. JSM CMP cascading select.
// We 'repair' the options by adding a uuid where it is missing.
// This ensures that the options are linked correctly.
const repairMissingUuids = (arr: ReadonlyArray<unknown>) =>
	arr.map((opt) => {
		if (opt != null && typeof opt === 'object') {
			return {
				...opt,
				externalUuid:
					// @ts-expect-error - TS2339 - Property 'externalUuid' does not exist on type 'object'. | TS2339 - Property 'externalUuid' does not exist on type 'object'. | TS2339 - Property 'externalUuid' does not exist on type 'object'.
					opt.externalUuid != null && opt.externalUuid !== '' ? opt.externalUuid : uuid(),
			};
		}
		return opt;
	});

const getUuidsMap = (
	arr: ReadonlyArray<unknown>,
): {
	[key: number]: OptionUuid | null;
} =>
	arr.reduce<{ [key: number]: OptionUuid | null }>((map, opt: unknown) => {
		if (
			opt != null &&
			typeof opt === 'object' &&
			// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'.
			typeof opt.id === 'number' &&
			// @ts-expect-error - TS2339 - Property 'externalUuid' does not exist on type 'object'.
			typeof opt.externalUuid === 'string'
		) {
			// @ts-expect-error - TS2339 - Property 'id' does not exist on type '{}'.
			const { id } = opt;
			return {
				// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
				...map,
				// @ts-expect-error - TS2339 - Property 'externalUuid' does not exist on type 'object'.
				[id]: toOptionUuid(opt.externalUuid) || null,
			};
		}
		return map;
	}, {});

export const normalizeOptions = (fieldType: string, arr: unknown): undefined | FieldOptions => {
	if (!doesFieldTypeSupportOptions(fieldType)) {
		return undefined;
	}

	if (!Array.isArray(arr)) {
		return [];
	}

	const options: OptionType[] = [];
	const repairedArr = repairMissingUuids(arr);
	const uuidsMap = getUuidsMap(repairedArr);

	repairedArr.forEach((o: unknown) => {
		if (
			o != null &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			typeof o.value === 'string' &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			typeof o.id === 'number' &&
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			!Number.isNaN(o.id)
		) {
			let externalUuid;
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			if (typeof o.externalUuid === 'string') {
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				externalUuid = toOptionUuid(o.externalUuid);
			}

			// @ts-expect-error - TS2571 - Object is of type 'unknown'. | TS2571 - Object is of type 'unknown'.
			const parentId = typeof o.parentId === 'number' ? o.parentId : null;
			// @ts-expect-error - TS2339 - Property 'value' does not exist on type 'unknown'.
			const { value, color, colour } = o;
			// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'unknown'.
			const { id } = o;
			// optimisticId is used for rendering options, DnD, etc... We map the option id to ensure this method
			// remains idempotent
			const optimisticId = id.toString();

			options.push({
				value,
				id,
				optimisticId,
				externalUuid,
				parentId,
				colour: color?.colorKey || colour,
				parentExternalUuid: parentId != null && uuidsMap[parentId] ? uuidsMap[parentId] : null,
			});
		} else {
			logError(
				PACKAGE_NAME,
				toErrorId('fieldsNormalizer.invalidOption'),
				toErrorMessage(`Option not defined or option has no "value" or no "id": ${printShape(o)}`),
			);

			throw new Error(
				'Option not defined or option has no "value" or no "id". (issue-type-page.services.issue-type.normalizer)',
			);
		}
	});

	return options;
};
